import { MdOutlineAutorenew } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { incomeForUser, sumForUser } from '../../../../lib/Calculator'
import { useFormatCurrency } from '../../../../lib/CurrencyFormatter'
import { UserType } from '../../../../lib/Enums/UserType'
import { useBudget } from '../../lib/Hooks/Budget'
import { useUpdateSheet } from '../../lib/Hooks/Sheet'
import { useExpenses, useIncomes, useSavings } from '../../lib/Hooks/Spends'
import BalancedAllowanceColumn from './BalancedAllowanceColumn'

const BalancedAllowance = () => {
  const { t } = useTranslation(['budget'])
  const formatCurrency = useFormatCurrency()
  const { data: budget } = useBudget()
  const { data: items } = useExpenses(budget!)
  const { data: incomes } = useIncomes(budget!)
  const { data: savings } = useSavings(budget!)
  const { mutate: updateBudget } = useUpdateSheet()

  // "Expenses" here includes savings too, matching the "Udgifter +
  // Opsparing" line in the main allowance table above this one.
  const expensesForPrimaryUser =
    sumForUser(items!, UserType.PRIMARY) +
    sumForUser(savings!, UserType.PRIMARY)
  const expensesForSecondaryUser =
    sumForUser(items!, UserType.SECONDARY) +
    sumForUser(savings!, UserType.SECONDARY)

  const incomeForPrimaryUser = incomeForUser(incomes!, UserType.PRIMARY)
  const incomeForSecondaryUser = incomeForUser(incomes!, UserType.SECONDARY)

  const allowanceForAuthor = incomeForPrimaryUser - expensesForPrimaryUser
  const allowanceForOtherUser =
    incomeForSecondaryUser - expensesForSecondaryUser

  const defaultBalancedAllowance =
    (allowanceForOtherUser + allowanceForAuthor) / 2

  const maximumBalancedAllowance = allowanceForAuthor + allowanceForOtherUser

  let balancedAllowanceForAuthor = defaultBalancedAllowance
  let balancedAllowanceForUser = defaultBalancedAllowance

  if (budget!.data.authorBalancedAllowance) {
    balancedAllowanceForAuthor = budget!.data.authorBalancedAllowance
    balancedAllowanceForUser = maximumBalancedAllowance - budget!.data.authorBalancedAllowance
  }
  if (budget!.data.userBalancedAllowance) {
    balancedAllowanceForAuthor = maximumBalancedAllowance - budget!.data.userBalancedAllowance
    balancedAllowanceForUser = budget!.data.userBalancedAllowance
  }

  const resetBalancedAllowance = () => {
    const newBudget = structuredClone(budget)
    newBudget!.data.authorBalancedAllowance = defaultBalancedAllowance
    newBudget!.data.userBalancedAllowance = defaultBalancedAllowance

    updateBudget(newBudget!)
  }

  const updateBalancedAllowance = (allowance: number, isAuthor: boolean) => {
    const propertyName = isAuthor
      ? 'authorBalancedAllowance'
      : 'userBalancedAllowance'
    const otherPropertyName = isAuthor
      ? 'userBalancedAllowance'
      : 'authorBalancedAllowance'

    const newBudget = structuredClone(budget)
    // Remove both properties from the data object, to ensure only the one being edited is set.
    delete newBudget!.data[propertyName]
    delete newBudget!.data[otherPropertyName]

    newBudget!.data[propertyName] = allowance

    updateBudget(newBudget!)
  }

  return (
    <>
      <tr className='bg-white border-x border-b border-gray-300'>
        <td className='text-sm text-gray-900 px-6 py-4'>
          <p className='font-bold flex'>{t('budget:balancedAllowance.title')}</p>
          <p className='italic font-light'>
            {t('budget:balancedAllowance.description1')}
          </p>
          <p className='italic font-light'>
            {t('budget:balancedAllowance.description2')}
          </p>
        </td>
        <td className='text-sm text-gray-900 font-bold px-6 py-4'>
          {formatCurrency(defaultBalancedAllowance)}
        </td>
        <td className='text-sm text-gray-900 font-bold px-6 py-4'>
          {formatCurrency(defaultBalancedAllowance)}
        </td>
      </tr>
      <tr className='bg-white border-x border-b border-gray-300'>
        <td className='text-sm text-gray-900 px-6 py-4'>
          <div className='flex items-center mb-2'>
            <p className='font-bold'>
              {t('budget:balancedAllowance.adjustedTitle')}
            </p>
            <div className="ml-2 flex bg-gray-200 px-2 py-1 border border-gray-300 rounded-xl cursor-pointer" onClick={resetBalancedAllowance}>
              <MdOutlineAutorenew
                size='1rem'
                className='mr-1 '
                title={t('budget:balancedAllowance.resetTitle')}
              />
              <span className="font-light text-xs">
                {t('budget:balancedAllowance.reset')}
              </span>
            </div>
          </div>
          <p className='italic font-light'>
            {t('budget:balancedAllowance.adjustedDescription')}
          </p>
        </td>
        <td className='text-sm text-gray-900 font-bold px-6 py-4'>
          <BalancedAllowanceColumn
            allowance={balancedAllowanceForAuthor}
            allowanceUpdated={(allowance: number) =>
              updateBalancedAllowance(allowance, true)
            }
            maximumAllowance={maximumBalancedAllowance}
          />
        </td>
        <td className='text-sm text-gray-900 font-bold px-6 py-4'>
          <BalancedAllowanceColumn
            allowance={balancedAllowanceForUser}
            allowanceUpdated={(allowance: number) =>
              updateBalancedAllowance(allowance, false)
            }
            maximumAllowance={maximumBalancedAllowance}
          />
        </td>
      </tr>

      <tr className='bg-white border-x border-b border-gray-300'>
        <td className='text-sm text-gray-900 font-light px-6 py-4'>
          <span className="font-bold">
            {t('budget:balancedAllowance.adjustedExpenses')}
          </span>
          <p className='italic'>
            {t('budget:balancedAllowance.adjustedExpensesDescription1')}
          </p>
          <p className='italic'>
            {t('budget:balancedAllowance.adjustedExpensesDescription2')}
          </p>
        </td>
        <td className='text-sm text-gray-900 font-light px-6 py-4'>
          {formatCurrency(incomeForPrimaryUser - balancedAllowanceForAuthor)}
        </td>
        <td className='text-sm text-gray-900 font-light px-6 py-4'>
          {formatCurrency(incomeForSecondaryUser - balancedAllowanceForUser)}
        </td>
      </tr>
    </>
  )
}

export default BalancedAllowance
